import { createCurrencyFormatter, messagingInDays } from 'corso-web-lib';
import { useMemo } from 'react';
import { useRouteLoaderData } from 'react-router';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { RouteIds } from './constants';
import { logError } from './observability';
import { gspOrderSchema } from './schemas';

const useOrderCurrency = () => {
  const data = useRouteLoaderData<unknown>(RouteIds.appShell);

  return useMemo(
    () =>
      match(
        z
          // can use the broader order schema here
          .object({ order: gspOrderSchema })
          .safeParse(data),
      )
        .with(
          { success: true, data: P.select() },
          ({ order }) => order.currencyCode,
        )
        .with({ success: false }, () => undefined)
        .exhaustive(),
    [data],
  );
};

const useClaimCurrency = () => {
  const data = useRouteLoaderData<unknown>(RouteIds.claimOverview);
  return useMemo(
    () =>
      match(z.object({ currencyCode: z.string() }).safeParse(data))
        .with(
          { success: true, data: P.select() },
          ({ currencyCode }) => currencyCode,
        )
        .with({ success: false }, () => undefined)
        .exhaustive(),
    [data],
  );
};

export { createCurrencyFormatter, messagingInDays };

export const formatProductOptions = <Option extends { value: string }>(
  options: Option[],
) =>
  options.length
    ? options
        .map(({ value }) => value)
        .filter((t) => t !== 'Default Title')
        .join(' / ')
    : '';

export const disjunctiveList = (items: string[]) =>
  new Intl.ListFormat(undefined, {
    style: 'long',
    type: 'disjunction',
  }).format(items);

/**
 * Provides a currency formatter based on the current order or claim currency.
 * If no order or claim currency is available, it defaults to USD.
 * The currency code is determined from the order or claim data loaded in the app shell loader or claim overview loader.
 * Uses the `useRouteLoaderData` hook to access the app shell or claim overview data based on the route IDs configured in the app routes.
 *
 * This implementation will couple any consumers to React Router and require any downstream usage to be
 * aware of React Router and the app shell or claim overview loaders and their IDs and may cause unexpected issues
 * if the app shell or claim overview loaders are changed.
 *
 * Components tests that have this hook within their downstream dependencies will need to be mock this hook,
 * or wrap in Route Provider to render. The `utils/testing.tsx` file provides a wrapper that can be used to render
 * a component with a Route Provider.
 * @returns A function that formats a number into a currency string.
 * @param amount - The amount to format.
 */
export const useCurrencyFormatter = () => {
  const orderCurrency = useOrderCurrency();
  const claimCurrency = useClaimCurrency();

  // If both currencies are available, prefer the order currency
  const currencyCode = orderCurrency ?? claimCurrency ?? 'USD';
  const currencyFormatter = useMemo(
    () => createCurrencyFormatter(currencyCode),
    [currencyCode],
  );

  return currencyFormatter;
};

const StandardDateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
});
const UTCDateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'UTC',
});
const invalidDatePlaceholder = '--';

const parseDate = (value: string) => {
  const date = new Date(value);

  if (date.toString() === 'Invalid Date') {
    logError(new Error(`Invalid date: ${value}`));
    return null;
  }

  return date;
};

export const formatDate = (value: string) => {
  const date = parseDate(value);

  if (!date) {
    return invalidDatePlaceholder;
  }

  try {
    return StandardDateFormatter.format(date);
  } catch (e) {
    logError(
      e instanceof Error ? e : new Error('Date formatting error', { cause: e }),
    );
    return invalidDatePlaceholder;
  }
};

export const formatDateTimeUtc = (value: string) => {
  const date = parseDate(value);

  if (!date) {
    return invalidDatePlaceholder;
  }

  try {
    return UTCDateFormatter.format(date);
  } catch (e) {
    logError(
      e instanceof Error
        ? e
        : new Error('DateTime UTC formatting error', { cause: e }),
    );
    return invalidDatePlaceholder;
  }
};
